import { writable } from 'svelte/store'
import { tenderFiltersApi } from '../api/tenderFilters'

const STORAGE_KEY = 'argus.activeTenderFilterId'

/** The CPV tender filter currently scoping "All Procurements" — undefined
 * means unfiltered (matches ProcurementFilters.filterId's "no silent active
 * filter" contract on the backend). Set once at startup by
 * initActiveFilterId(), then only by the user picking a filter. */
export const activeFilterId = writable<number | undefined>(undefined)

let initialized = false

export function setActiveFilterId(id: number | undefined): void {
  activeFilterId.set(id)
  try {
    if (id === undefined) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, String(id))
  } catch {
    // localStorage can throw (private browsing, blocked site data) — the
    // in-memory store still works for the rest of this session.
  }
}

/**
 * Resolves the initially-active filter, in priority order: an explicit URL
 * override, then the last choice remembered in this browser, then the
 * current default filter. Safe to call from more than one view (Dashboard,
 * Archive, Filters) — only the first call does any work, so a later view
 * mounting after the user already picked a filter doesn't clobber it.
 */
export async function initActiveFilterId(urlFilterId?: number): Promise<void> {
  if (initialized) return
  initialized = true

  if (urlFilterId !== undefined) {
    activeFilterId.set(urlFilterId)
    return
  }

  const stored = readStoredId()
  if (stored !== undefined) {
    activeFilterId.set(stored)
    return
  }

  try {
    const filters = await tenderFiltersApi.list()
    const defaultFilter = filters.find((f) => f.isDefault)
    if (defaultFilter) activeFilterId.set(defaultFilter.id)
  } catch {
    // No active filter resolved (e.g. offline at startup) — All Procurements
    // falls back to unfiltered rather than blocking on a filter pick.
  }
}

function readStoredId(): number | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  } catch {
    return undefined
  }
}
